/**
 * Hue Bridge Discovery Service
 * Handles discovering Hue bridges on local network (Tauri) or via cloud (Web)
 */

import { Platform } from '@/lib/platform'
import { hueLogger } from '@/lib/logger'
import { httpGet } from '@/lib/http'

export interface DiscoveredBridge {
  id: string
  internalipaddress: string
  port?: number
  name?: string
  modelid?: string
  swversion?: string
  bridgeid?: string
  // Additional discovery metadata
  discoveryMethod: 'mdns' | 'nupnp' | 'manual'
  lastSeen: number
}

export interface BridgeDiscoveryOptions {
  timeout?: number // Discovery timeout in milliseconds
  includeLocal?: boolean // Include local network discovery
  includeCloud?: boolean // Include cloud discovery
}

export class HueDiscoveryService {
  private static readonly HUE_DISCOVERY_URL = 'https://discovery.meethue.com/'
  private static readonly DEFAULT_TIMEOUT = 10000 // 10 seconds

  /**
   * Get the appropriate discovery URL based on environment
   */
  private getDiscoveryUrl(): string {
    const isDesktop = Platform.isDesktop()
    const isDev = import.meta.env.DEV
    
    hueLogger.debug(`Discovery URL context: isDesktop=${isDesktop}, isDev=${isDev}`)
    
    // In Tauri, always use the direct URL (Tauri can make HTTPS requests)
    if (isDesktop) {
      hueLogger.debug(`Using direct discovery URL: ${HueDiscoveryService.HUE_DISCOVERY_URL}`)
      return HueDiscoveryService.HUE_DISCOVERY_URL
    }
    // In web development mode, use the proxy to avoid CORS
    if (isDev) {
      hueLogger.debug('Using proxy discovery URL: /api/hue-discovery')
      return '/api/hue-discovery'
    }
    // In web production, use the direct URL
    hueLogger.debug(`Using direct discovery URL: ${HueDiscoveryService.HUE_DISCOVERY_URL}`)
    return HueDiscoveryService.HUE_DISCOVERY_URL
  }

  /**
   * Discover Hue bridges using appropriate method for platform
   */
  async discoverBridges(options: BridgeDiscoveryOptions = {}): Promise<DiscoveredBridge[]> {
    const {
      timeout = HueDiscoveryService.DEFAULT_TIMEOUT,
      includeLocal = Platform.hasCapability('localDiscovery'),
      includeCloud = true
    } = options

    const discoveries: Promise<DiscoveredBridge[]>[] = []

    // Local network discovery (Tauri only)
    if (includeLocal && Platform.isDesktop()) {
      discoveries.push(this.discoverViaMDNS())
      discoveries.push(this.discoverViaUPnP())
    }

    // Cloud discovery (both platforms)
    if (includeCloud) {
      discoveries.push(this.discoverViaCloud(timeout))
    }

    try {
      // Run all discovery methods in parallel
      const results = await Promise.allSettled(discoveries)
      const bridges: DiscoveredBridge[] = []

      for (const result of results) {
        if (result.status === 'fulfilled') {
          bridges.push(...result.value)
        } else {
          hueLogger.warn('Bridge discovery method failed:', result.reason)
        }
      }

      // Deduplicate bridges by IP address
      const uniqueBridges = this.deduplicateBridges(bridges)
      
      hueLogger.info(`Discovered ${uniqueBridges.length} Hue bridges`)
      return uniqueBridges
    } catch (error) {
      hueLogger.error('Bridge discovery failed:', error)
      return []
    }
  }

  /**
   * Discover bridges via Philips cloud service (NUPNP)
   */
  private async discoverViaCloud(timeout: number): Promise<DiscoveredBridge[]> {
    try {
      const response = await httpGet<Array<Record<string, unknown>>>(
        this.getDiscoveryUrl(),
        {
          timeout,
          headers: {
            'Accept': 'application/json'
          }
        }
      )

      if (!response.ok) {
        throw new Error(`Cloud discovery failed: ${response.status}`)
      }

      const bridges = response.data
      
      return bridges.map((bridge, index) => ({
        id: String(bridge.id || `cloud_${index}`),
        internalipaddress: String(bridge.internalipaddress || ''),
        port: Number(bridge.port) || 80,
        name: String(bridge.name || ''),
        modelid: String(bridge.modelid || ''),
        swversion: String(bridge.swversion || ''),
        bridgeid: String(bridge.id || ''),
        discoveryMethod: 'nupnp' as const,
        lastSeen: Date.now()
      }))
    } catch (error) {
      hueLogger.error('Cloud discovery failed:', error)
      return []
    }
  }

  /**
   * Discover bridges via mDNS (Tauri only)
   */
  private async discoverViaMDNS(): Promise<DiscoveredBridge[]> {
    if (!Platform.isDesktop()) {
      return []
    }

    try {
      // This would use Tauri's HTTP client to scan for mDNS services
      // For now, return empty array as this requires additional Tauri setup
      hueLogger.debug('mDNS discovery not yet implemented')
      return []
    } catch (error) {
      hueLogger.error('mDNS discovery failed:', error)
      return []
    }
  }

  /**
   * Discover bridges via UPnP (Tauri only)
   */
  private async discoverViaUPnP(): Promise<DiscoveredBridge[]> {
    if (!Platform.isDesktop()) {
      return []
    }

    try {
      // This would scan local network for UPnP devices
      // For now, return empty array as this requires additional implementation
      hueLogger.debug('UPnP discovery not yet implemented')
      return []
    } catch (error) {
      hueLogger.error('UPnP discovery failed:', error)
      return []
    }
  }

  /**
   * Test if a specific IP address has a Hue bridge
   */
  async testBridgeAtIP(ipAddress: string): Promise<DiscoveredBridge | null> {
    try {
      const response = await fetch(`http://${ipAddress}/api/config`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000)
      })

      if (!response.ok) {
        return null
      }

      const config = await response.json()
      
      // Check if this looks like a Hue bridge
      if (config.bridgeid && config.modelid) {
        return {
          id: config.bridgeid,
          internalipaddress: ipAddress,
          port: 80,
          name: config.name || 'Philips hue',
          modelid: config.modelid,
          swversion: config.swversion,
          bridgeid: config.bridgeid,
          discoveryMethod: 'manual',
          lastSeen: Date.now()
        }
      }

      return null
    } catch (error) {
      hueLogger.error(`Failed to test bridge at ${ipAddress}:`, error)
      return null
    }
  }

  /**
   * Add a bridge manually by IP address
   */
  async addBridgeByIP(ipAddress: string): Promise<DiscoveredBridge | null> {
    // Validate IP address format
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
    
    if (!ipRegex.test(ipAddress)) {
      throw new Error('Invalid IP address format')
    }

    return await this.testBridgeAtIP(ipAddress)
  }

  /**
   * Remove duplicate bridges based on IP address
   */
  private deduplicateBridges(bridges: DiscoveredBridge[]): DiscoveredBridge[] {
    const uniqueMap = new Map<string, DiscoveredBridge>()

    for (const bridge of bridges) {
      const key = bridge.internalipaddress
      const existing = uniqueMap.get(key)

      if (!existing || bridge.lastSeen > existing.lastSeen) {
        // Prefer more recent discovery or more complete information
        uniqueMap.set(key, bridge)
      }
    }

    return Array.from(uniqueMap.values())
  }

  /**
   * Scan local network for potential bridges (Tauri only)
   * This is a simplified implementation - in a real app you'd use proper network scanning
   */
  async scanLocalNetwork(): Promise<DiscoveredBridge[]> {
    if (!Platform.isDesktop()) {
      return []
    }

    try {
      // Get local network range (simplified - assumes 192.168.x.x)
      const commonRanges = [
        '192.168.1.',
        '192.168.0.',
        '192.168.2.',
        '10.0.0.',
        '10.0.1.'
      ]

      const bridges: DiscoveredBridge[] = []

      for (const range of commonRanges) {
        // Test a few common IP addresses in each range
        const testIPs = [1, 2, 3, 4, 5, 10, 100, 101, 102].map(i => `${range}${i}`)
        
        const tests = testIPs.map(ip => this.testBridgeAtIP(ip))
        const results = await Promise.allSettled(tests)

        for (const result of results) {
          if (result.status === 'fulfilled' && result.value) {
            bridges.push(result.value)
          }
        }
      }

      return bridges
    } catch (error) {
      hueLogger.error('Local network scan failed:', error)
      return []
    }
  }
}

// Singleton instance
export const hueDiscoveryService = new HueDiscoveryService()